import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/App';
import { Button } from '@/components/ui/button';
import { Building2, Calendar, Clock, ArrowLeft, Share2, Linkedin, Twitter, Facebook } from 'lucide-react';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';

const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const response = await apiClient.get(`/blog/articles/${slug}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Article non trouvé:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen bg-white">
      {/* SEO + Schema.org */}
      <Helmet>
        <title>{article.meta_title || article.title} | Blog copropriete.app</title>
        <meta name="description" content={article.meta_description || article.excerpt} />
        <meta name="keywords" content={article.keywords?.join(', ')} />
        <meta name="author" content="copropriete.app" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://copropriete.app/blog/${article.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://copropriete.app/blog/${article.slug}`} />
        <meta property="og:site_name" content="copropriete.app" />
        <meta property="og:locale" content="fr_FR" />
        {article.image_url && <meta property="og:image" content={article.image_url} />}
        {article.image_alt && <meta property="og:image:alt" content={article.image_alt} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.meta_description || article.excerpt} />
        {article.image_url && <meta name="twitter:image" content={article.image_url} />}
        
        {/* Schema.org Article */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.meta_description || article.excerpt,
            "image": article.image_url,
            "author": {
              "@type": "Organization",
              "name": "copropriete.app",
              "url": "https://copropriete.app"
            },
            "publisher": {
              "@type": "Organization",
              "name": "copropriete.app",
              "logo": {
                "@type": "ImageObject",
                "url": "https://copropriete.app/logo.png"
              }
            },
            "datePublished": article.created_at,
            "dateModified": article.updated_at || article.created_at,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://copropriete.app/blog/${article.slug}`
            },
            "keywords": article.keywords?.join(', '),
            "articleSection": article.category,
            "inLanguage": "fr-FR"
          })}
        </script>
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">copropriete.app</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/blog')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au blog
              </Button>
              <Button onClick={() => navigate('/register')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Essai gratuit
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-slate-500 mb-8">
            <Link to="/" className="hover:text-emerald-600">Accueil</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-emerald-600">Blog</Link>
            <span>/</span>
            <span className="text-slate-900">{article.title}</span>
          </nav>

          {/* Category & Meta */}
          <div className="flex items-center gap-4 mb-6">
            <span className="px-4 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
              {article.category === 'guide' ? 'Guide' : 
               article.category === 'juridique' ? 'Juridique' :
               article.category === 'conseils' ? 'Conseils' : 'Actualités'}
            </span>
            <span className="flex items-center gap-1.5 text-slate-500 text-sm">
              <Calendar className="w-4 h-4" />
              {new Date(article.created_at).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
            <span className="flex items-center gap-1.5 text-slate-500 text-sm">
              <Clock className="w-4 h-4" />
              {article.read_time} min de lecture
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            {article.title}
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-slate-600 mb-8">
            {article.excerpt}
          </p>

          {/* Featured Image */}
          {article.image_url && (
            <figure className="my-12">
              <div className="aspect-video bg-slate-100 rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={article.image_url} 
                  alt={article.image_alt || article.title}
                  title={article.image_alt || article.title}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              {article.image_alt && (
                <figcaption className="text-center text-sm text-slate-500 mt-3">
                  {article.image_alt}
                </figcaption>
              )}
            </figure>
          )}

          {/* Content - Style de lecture amélioré */}
          <article 
            className="prose prose-lg prose-slate max-w-none 
              prose-headings:font-bold prose-headings:text-slate-900
              prose-h1:text-3xl prose-h1:mt-8 prose-h1:mb-6
              prose-h2:text-2xl prose-h2:mt-14 prose-h2:mb-6 prose-h2:pt-8 prose-h2:border-t-2 prose-h2:border-emerald-100
              prose-h3:text-xl prose-h3:mt-10 prose-h3:mb-4 prose-h3:text-emerald-800
              prose-h4:text-lg prose-h4:mt-8 prose-h4:mb-3 prose-h4:text-slate-700
              prose-p:text-slate-600 prose-p:leading-8 prose-p:mb-6
              prose-a:text-emerald-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline 
              prose-strong:text-slate-800 prose-strong:font-semibold
              prose-ul:my-8 prose-ul:space-y-3 prose-ul:text-slate-600
              prose-ol:my-8 prose-ol:space-y-3 prose-ol:text-slate-600
              prose-li:marker:text-emerald-500 prose-li:leading-7
              prose-blockquote:border-l-4 prose-blockquote:border-emerald-500 prose-blockquote:bg-emerald-50/50 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-blockquote:text-slate-700 prose-blockquote:my-8
              prose-hr:my-16 prose-hr:border-slate-200
              prose-code:bg-slate-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-emerald-700 prose-code:text-sm
              [&>p:first-of-type]:text-xl [&>p:first-of-type]:text-slate-700 [&>p:first-of-type]:leading-8 [&>p:first-of-type]:font-medium
            "
          >
            {/* Détection auto: si contient des balises HTML, utiliser dangerouslySetInnerHTML, sinon ReactMarkdown */}
            {article.content.includes('<h2>') || article.content.includes('<p>') ? (
              <div dangerouslySetInnerHTML={{ __html: article.content }} />
            ) : (
              <ReactMarkdown>{article.content}</ReactMarkdown>
            )}
          </article>

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-900">Partager cet article</span>
              <div className="flex gap-3">
                <a 
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(article.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 hover:bg-emerald-100 hover:text-emerald-600 transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 hover:bg-emerald-100 hover:text-emerald-600 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 hover:bg-emerald-100 hover:text-emerald-600 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-3">
              Prêt à simplifier votre gestion de copropriété ?
            </h3>
            <p className="text-emerald-100 mb-6">
              Essayez copropriete.app gratuitement pendant 7 jours
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-white text-emerald-700 hover:bg-emerald-50"
            >
              Créer mon compte gratuit
            </Button>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">copropriete.app</span>
          </div>
          <p className="text-sm">© 2026 copropriete.app. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogArticle;
